// Lion Auto "Reprice after a sale" per show and per artist: a group on Auto
// re-ladders within minutes after one of its listings sells only while its
// event's setting is on. This show's setting wins, else its artist's (the
// Skybox performer: every show of it), else the runner's default
// (lion_auto_config sale_refill, off). Each is On, Off or Default (no
// setting of its own). The server resolves it; these helpers word the
// Reprice after a sale box (LaSaleRefillPanel). Pure; tested in
// lionAutoSaleRefill.test.js.
import { detailLines, retryAfterSec } from './lionAutoErrors';

// The sources the server resolves from (GET sale-refill: effective.source)
const SOURCE_SHOW = 'show';
const SOURCE_ARTIST = 'artist';
const SOURCE_DEFAULT = 'default';

const nameOf = (performerName) => (typeof performerName === 'string' ? performerName.trim() : '');
const onOff = (on) => (on ? 'on' : 'off');

/**
 * A setting as its select option: true 'on', false 'off', anything else
 * 'default' (no setting of its own; junk never reads as on).
 * @param {*} value  GET sale-refill: show, or artist.value
 * @returns {'default'|'on'|'off'}
 */
export function choiceOf(value) {
  if (value === true) return 'on';
  if (value === false) return 'off';
  return 'default';
}

/**
 * A select option as the PUT body's value: 'on' true, 'off' false, anything
 * else null (back to the default).
 * @param {*} choice
 * @returns {true|false|null}
 */
export function valueOfChoice(choice) {
  if (choice === 'on') return true;
  if (choice === 'off') return false;
  return null;
}

/**
 * The Default option's words: what a show or artist with no setting of its
 * own gets. "Default (off)" for the runner's default, "Default (on · Jonas
 * Brothers)" for the artist's; "Default" while unknown.
 * @param {*} inheritedOn  the inherited setting (a boolean when known)
 * @param {string|null} [from]  the performer it comes from; none for the runner's default
 */
export function defaultChoiceText(inheritedOn, from = null) {
  if (inheritedOn !== true && inheritedOn !== false) return 'Default';
  const name = nameOf(from);
  return `Default (${onOff(inheritedOn)}${name ? ` · ${name}` : ''})`;
}

/**
 * The "This show only" row's Default option: the artist's setting when it
 * has one (named), else the runner's default. This show's own setting
 * plays no part: Default is what the show gets without it.
 * @param {{ default: boolean, artist: { name, value }|null }|null} view  GET sale-refill
 */
export function showDefaultText(view) {
  const artist = view && typeof view === 'object' ? view.artist : null;
  const name = nameOf(artist?.name);
  if (name && choiceOf(artist.value) !== 'default') return defaultChoiceText(artist.value, name);
  return defaultChoiceText(view?.default);
}

/**
 * What applies to this show, and from where: "On · this show", "Off · Jonas
 * Brothers (all shows)", "Off · default"; "" while unknown.
 * @param {{ on: boolean, source: 'show'|'artist'|'default' }|null} effective  GET sale-refill: effective
 * @param {string|null} artistName  the show's performer (artist.name)
 */
export function effectiveText(effective, artistName) {
  const on = effective && typeof effective === 'object' ? effective.on : undefined;
  if (on !== true && on !== false) return '';
  const head = on ? 'On' : 'Off';
  if (effective.source === SOURCE_SHOW) return `${head} · this show`;
  if (effective.source === SOURCE_ARTIST) return `${head} · ${nameOf(artistName) || 'artist'} (all shows)`;
  if (effective.source === SOURCE_DEFAULT) return `${head} · default`;
  return head;
}

const NO_ANSWER = 'The server did not answer: reload to see whether it saved.';
const SAVE_FAILED = 'Saving the setting failed.';
const READ_FAILED = 'Could not read Reprice after a sale.';

/**
 * A failed sale-refill call in words: the contract's codes
 * (SALE_REFILL_INVALID with its details, ARTIST_UNKNOWN, ADMIN_ONLY), then
 * the generic answers.
 * @param {object} err  the axios error
 * @param {{ read?: boolean }} [opts]  read: a GET (its own fallback, no "whether it saved")
 */
export function saleRefillErrorText(err, { read = false } = {}) {
  const status = err?.response?.status;
  const data = err?.response?.data || {};
  const fallback = read ? READ_FAILED : SAVE_FAILED;
  if (status === 429) return `Too many requests — try again in ${retryAfterSec(err)} s.`;
  if (data.code === 'SALE_REFILL_INVALID') {
    const lines = detailLines(data.details);
    return `The setting was not accepted${lines.length ? `: ${lines.join('; ')}` : '.'}`;
  }
  if (data.code === 'ARTIST_UNKNOWN') {
    return "This show's performer is not known, so it has no artist setting. Set it for this show only instead.";
  }
  if (status === 403) {
    return data.code === 'ADMIN_ONLY' ? 'Only an admin can change Reprice after a sale.' : 'You do not have access to this event.';
  }
  // A bare { error } with no code is the server's unknown-route answer
  if (status === 404) return data.code ? (data.error || 'Not found.') : 'Reprice after a sale per show is not available on this server yet.';
  if (status === 400) return data.error || 'The setting was not accepted.';
  if (!err) return fallback;
  if (!status) return read ? fallback : NO_ANSWER;
  return data.error || fallback;
}
